/* ==========================================================================
   RAM Bikes — cart drawer
   ==========================================================================
   Opens from the header cart icon and after every add-to-cart; re-renders
   itself through the Section Rendering API (?sections=cart-drawer) so the
   Liquid in sections/cart-drawer.liquid stays the single source of markup.

   Modal behaviour mirrors nav.js: focus trap, Escape, backdrop, scroll lock
   with scrollbar compensation, focus restore, close on bfcache restore.
   Without JS: the cart icon is a plain link to /cart and the product form
   posts to /cart/add — nothing here is load-bearing.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-cart-drawer]');
  if (!root) return;

  var panel = root.querySelector('.cart-drawer__panel');
  var docEl = document.documentElement;
  var routes = (window.RAM_CONFIG && window.RAM_CONFIG.routes) || {};
  var CART = routes.cart || '/cart';
  var SECTION = 'cart-drawer';

  var isOpen = false;
  var lastFocused = null;
  var closeTimer = null;
  var pending = null;

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function inner() { return root.querySelector('[data-cart-drawer-inner]'); }
  function reduced() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }

  function isVisible(el) {
    if (typeof el.checkVisibility === 'function') return el.checkVisibility({ checkVisibilityCSS: true });
    return el.getClientRects().length > 0;
  }
  function focusables() {
    return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), isVisible);
  }

  function lockScroll() {
    var gap = window.innerWidth - docEl.clientWidth;
    docEl.style.setProperty('--scrollbar-w', gap > 0 ? gap + 'px' : '0px');
    docEl.classList.add('is-drawer-open');
  }
  function unlockScroll() {
    docEl.classList.remove('is-drawer-open');
    docEl.style.removeProperty('--scrollbar-w');
  }

  function open() {
    if (isOpen) return;
    isOpen = true;
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    lastFocused = document.activeElement;
    root.hidden = false;
    lockScroll();
    void root.offsetWidth;                       // commit [hidden] removal before the transition
    root.classList.add('is-open');
    var closeBtn = root.querySelector('[data-cart-close].cart-drawer__close');
    (closeBtn || panel).focus();
    document.addEventListener('keydown', onKeydown, true);
  }

  function close(options) {
    if (!isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown, true);
    unlockScroll();
    if (!(options && options.silent)) {
      var target = (lastFocused && document.contains(lastFocused)) ? lastFocused : document.querySelector('[data-cart-link]');
      if (target) target.focus();
    } else if (panel.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    closeTimer = setTimeout(function () { root.hidden = true; closeTimer = null; }, reduced() ? 0 : 240);
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) { event.preventDefault(); return; }
    var first = items[0], last = items[items.length - 1], active = document.activeElement;
    if (!panel.contains(active)) { event.preventDefault(); first.focus(); }
    else if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
  }

  /* ---- rendering ---------------------------------------------------------- */
  function swap(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-cart-drawer-inner]');
    var current = inner();
    if (!fresh || !current) return;
    var hadFocus = panel.contains(document.activeElement);
    current.replaceWith(fresh);
    var count = fresh.getAttribute('data-count') || '0';
    root.setAttribute('data-count', count);
    updateHeader(count);
    if (hadFocus) {
      var closeBtn = root.querySelector('.cart-drawer__close');
      if (closeBtn) closeBtn.focus();
    }
  }

  function updateHeader(count) {
    var n = Number(count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-empty', n === 0);
    });
    var label = document.querySelector('[data-cart-count-label]');
    if (label) label.textContent = label.textContent.replace(/\d+/, n);
  }

  function setBusy(on) {
    var el = inner();
    if (el) el.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function showError(message) {
    var el = root.querySelector('[data-cart-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /* Refresh from the current page: any storefront URL answers ?sections=. */
  function refresh() {
    var url = window.location.pathname + '?sections=' + SECTION;
    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (json) { if (json[SECTION]) swap(json[SECTION]); })
      .catch(function () { /* keep what we have */ });
  }

  /* ---- cart mutations ------------------------------------------------------ */
  function change(line, quantity) {
    if (pending) pending.abort && pending.abort();
    var controller = ('AbortController' in window) ? new AbortController() : null;
    pending = controller;
    setBusy(true);
    showError('');
    return fetch(CART + '/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity, sections: SECTION, sections_url: window.location.pathname }),
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
      .then(function (res) {
        if (!res.ok) { showError(res.json.description || res.json.message || 'Error'); setBusy(false); return refresh(); }
        if (res.json.sections && res.json.sections[SECTION]) swap(res.json.sections[SECTION]);
        else return refresh();
      })
      .catch(function (err) { if (err.name !== 'AbortError') { setBusy(false); refresh(); } });
  }

  function add(form, button) {
    var data = new FormData(form);
    data.append('sections', SECTION);
    data.append('sections_url', window.location.pathname);
    var errorEl = form.querySelector('[data-add-error]');
    if (errorEl) errorEl.hidden = true;
    if (button) { button.setAttribute('aria-busy', 'true'); button.disabled = true; }
    return fetch((routes.cartAdd || '/cart/add') + '.js', { method: 'POST', body: data, headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json().then(function (json) { return { ok: r.ok, json: json }; }); })
      .then(function (res) {
        if (!res.ok) {
          var msg = res.json.description || res.json.message || 'Error';
          if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; } else { showError(msg); open(); }
          return;
        }
        if (res.json.sections && res.json.sections[SECTION]) swap(res.json.sections[SECTION]);
        open();
        if (!(res.json.sections && res.json.sections[SECTION])) refresh();
        document.dispatchEvent(new CustomEvent('ram:cart:added', { detail: res.json }));
      })
      .catch(function () { form.submit(); })   // network trouble: let the native POST do it
      .finally(function () { if (button) { button.removeAttribute('aria-busy'); button.disabled = false; } });
  }

  /* ---- wiring (delegated so re-rendered markup needs no rebinding) ------- */
  document.addEventListener('click', function (event) {
    var link = event.target.closest('[data-cart-link]');
    if (link) { event.preventDefault(); open(); refresh(); return; }

    if (!root.contains(event.target)) return;

    if (event.target.closest('[data-cart-close]')) { close(); return; }

    var item = event.target.closest('.cart-drawer__item');
    if (!item) return;
    var line = Number(item.getAttribute('data-line'));
    var input = item.querySelector('[data-qty-input]');
    var qty = input ? Number(input.value) || 0 : 1;

    if (event.target.closest('[data-qty-minus]')) change(line, Math.max(0, qty - 1));
    else if (event.target.closest('[data-qty-plus]')) change(line, qty + 1);
    else if (event.target.closest('[data-remove]')) change(line, 0);
  });

  var typeTimer = null;
  root.addEventListener('input', function (event) {
    var input = event.target.closest('[data-qty-input]');
    if (!input) return;
    clearTimeout(typeTimer);
    typeTimer = setTimeout(function () {
      var item = input.closest('.cart-drawer__item');
      if (item) change(Number(item.getAttribute('data-line')), Math.max(0, Number(input.value) || 0));
    }, 500);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    var button = form.querySelector('[type="submit"]');
    if (button && button.disabled) { event.preventDefault(); return; }
    event.preventDefault();
    add(form, button);
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) { if (isOpen) close({ silent: true }); refresh(); }
  });

  // Apps that change the cart through the AJAX API announce it with one of
  // these events; re-render so the drawer and the count stay in step.
  ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (name) {
    document.addEventListener(name, function () { refresh(); });
  });

  window.RAMCart = { open: open, close: close, refresh: refresh, change: change };
})();
